/**
 * VideoStage Component
 *
 * In-page video player for the new learning page.
 * Uses QueturePlayer from @quebon/shared-learning for HLS video playback.
 *
 * API Integration (LEARNING_PROGRESS_API_PLAN.md):
 * - recordVideoHeartbeat: 5초 간격 위치 업데이트 (video_session_progress만)
 * - recordVideoEvent: 이벤트 발생 시 로그 기록 (video_clip_log - 시청 패턴 분석용)
 *   - play: 재생 시작
 *   - pause: 일시정지
 *   - seek: 구간 이동
 *   - end: 영상 종료/이탈
 * - completeSession: 학습 완료 처리
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { QueturePlayer } from '@quebon/shared-learning'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import type { QuetureApiInterface } from '@quebon/shared-learning'
import { contentApi } from '@/api/content'
import {
    learningSessionApi,
    VideoDataResponse,
    CompleteSessionResponse,
    VideoEventType,
} from '@/api/learningSession'
import {
    Play,
    RotateCcw,
    CheckCircle,
    ArrowLeft,
    Loader2,
} from 'lucide-react'
import { toast } from 'sonner'

// Default completion threshold (90%) - can be overridden by configuration
const DEFAULT_requiredWatchPercent = 90
// Heartbeat interval (5 seconds) - per LEARNING_PROGRESS_API_PLAN.md
const HEARTBEAT_INTERVAL_MS = 5000

export interface VideoStageProps {
    lessonId: number
    lessonTitle: string
    componentProgressId: number
    videoData: VideoDataResponse
    requiredWatchPercent?: number  // Configuration에서 전달 (기본값: 90)
    classProductId?: number        // B2B: Configuration 조회용 (레슨 완료 판단에 필요)
    onComplete: (response: CompleteSessionResponse) => void
    onBack: () => void
}

export function VideoStage({
    lessonId,
    lessonTitle,
    componentProgressId,
    videoData,
    requiredWatchPercent = DEFAULT_requiredWatchPercent,
    classProductId,
    onComplete,
    onBack,
}: VideoStageProps) {
    const { t } = useTranslation('learner')
    // State
    // 2회차 이상: 이전에 이미 필수 시청률을 달성한 경우 (완료 버튼 없이 자유 시청)
    const isRewatch = (videoData.progressPercent || 0) >= requiredWatchPercent

    // QueturePlayer API adapter (fallback - hlsUrl이 직접 전달되므로 대부분 사용되지 않음)
    const quetureApiAdapter: QuetureApiInterface = useMemo(() => ({
        getQuetureInfo: async (qid: string) => contentApi.getQuetureInfo(lessonId, qid),
    }), [lessonId])

    const [videoPlayKey, setVideoPlayKey] = useState(0)
    const [playerStartTime, setPlayerStartTime] = useState(
        isRewatch && videoData.skipAllowed ? 0 : (videoData.lastPositionSec || 0)
    )
    const [currentProgress, setCurrentProgress] = useState(videoData.progressPercent || 0)
    const [maxProgress, setMaxProgress] = useState(videoData.progressPercent || 0)
    const [currentTime, setCurrentTime] = useState(videoData.lastPositionSec || 0)
    const [duration, setDuration] = useState(videoData.duration || 0)
    const [totalWatchTime, setTotalWatchTime] = useState(0)
    const [isVideoCompleted, setIsVideoCompleted] = useState(false)
    const [isCompletionConfirmed, setIsCompletionConfirmed] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showCompletionActions, setShowCompletionActions] = useState(false)
    const [playbackSpeed, setPlaybackSpeed] = useState(1.0)

    // Refs for heartbeat and event tracking
    const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const lastHeartbeatTimeRef = useRef<number>(videoData.lastPositionSec || 0)
    const lastHeartbeatTimestampRef = useRef<number>(Date.now())
    const watchedRemainderRef = useRef<number>(0) // 소수점 잔여분 누적 (초)
    const currentTimeRef = useRef<number>(currentTime)
    const durationRef = useRef<number>(duration)
    const isPlayingRef = useRef<boolean>(false)
    const playbackSpeedRef = useRef<number>(1.0)
    const lastEventSentRef = useRef<{type: VideoEventType, time: number} | null>(null)
    // 구간 시작 위치 추적 (play 이벤트 시점)
    const playStartPositionRef = useRef<number>(videoData.lastPositionSec || 0)
    // Ref for sendHeartbeat to avoid interval reset on re-render
    const sendHeartbeatRef = useRef<(force?: boolean) => Promise<void>>()

    // Keep refs in sync
    useEffect(() => {
        currentTimeRef.current = currentTime
    }, [currentTime])

    useEffect(() => {
        durationRef.current = duration
    }, [duration])

    useEffect(() => {
        playbackSpeedRef.current = playbackSpeed
    }, [playbackSpeed])

    // Determine video source
    // - If videoUrl is available from Learning Session API, use it directly (preferred)
    // - Otherwise, fallback to bundleId as QID for legacy API flow
    const hasDirectUrl = !!videoData.videoUrl && videoData.videoUrl.trim() !== ''
    const videoQid = videoData.bundleId || ''
    const hasVideo = hasDirectUrl || (!!videoQid && videoQid.trim() !== '')

    // Convert relative API path to full URL for HLS.js
    // Backend returns: /api/content/{contentId}/quetures/{qid}/hls
    // We need to convert to: http://localhost:8080/api/content/{contentId}/quetures/{qid}/hls
    const hlsUrl = (() => {
        if (!videoData.videoUrl) return null
        // If already a full URL, use as-is
        if (videoData.videoUrl.startsWith('http')) return videoData.videoUrl
        // Convert relative path to full URL using current origin
        // VITE_API_BASE_URL could be /api or http://localhost:8080/api
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
        if (apiBaseUrl.startsWith('http')) {
            // Full URL - extract origin and append path
            const url = new URL(apiBaseUrl)
            return `${url.origin}${videoData.videoUrl}`
        }
        // Relative path - use current origin
        return `${window.location.origin}${videoData.videoUrl}`
    })()

    // Heartbeat function - sends position update to server
    // Only updates video_session_progress (position tracking)
    // video_clip_log is handled by sendVideoEvent (event-based logging)
    // force=true: 강제 전송 (pause/이탈 시)
    const sendHeartbeat = useCallback(async (force: boolean = false) => {
        if (!componentProgressId) return

        // 영상 완료 후에는 위치 저장용 force만 허용, watchedDuration은 보내지 않음
        if (showCompletionActions && !force) return

        const currentPos = currentTimeRef.current
        const lastPos = lastHeartbeatTimeRef.current

        // Skip if no meaningful position
        if (currentPos <= 0) return

        // Skip if position hasn't changed (unless forced)
        // timestamp는 리셋하여 다음 heartbeat의 watchedDuration이 비정상적으로 커지는 것을 방지
        if (!force && Math.abs(currentPos - lastPos) < 1) {
            lastHeartbeatTimestampRef.current = Date.now()
            watchedRemainderRef.current = 0
            return
        }

        // Calculate watched duration using wall clock time (배속과 무관하게 실제 경과 시간)
        // force heartbeat(pause/end)은 위치 저장만, 시간 누적은 interval heartbeat에서만 담당
        const now = Date.now()
        let cappedDuration: number | undefined = undefined

        if (!force) {
            // 소수점 잔여분을 포함하여 정확한 시청 시간 계산
            const rawSeconds = (now - lastHeartbeatTimestampRef.current) / 1000 + watchedRemainderRef.current
            const toSend = Math.floor(rawSeconds) // 정수부만 전송
            watchedRemainderRef.current = rawSeconds - toSend // 소수점 잔여분 보존

            cappedDuration = toSend > 0 && !showCompletionActions
                ? Math.min(toSend, HEARTBEAT_INTERVAL_MS / 1000 * 2)
                : undefined
            // interval heartbeat만 timestamp 업데이트 (force는 건드리지 않아 interval 주기 보존)
            lastHeartbeatTimestampRef.current = now
        }

        lastHeartbeatTimeRef.current = currentPos

        try {
            const response = await learningSessionApi.recordVideoHeartbeat(
                componentProgressId,
                {
                    currentPositionSec: Math.floor(currentPos),
                    watchedDurationSec: cappedDuration,
                    totalDurationSec: Math.floor(durationRef.current),
                    requiredWatchPercent,
                    classProductId,
                }
            )

            // Update state from server response
            if (response.progressPercent > maxProgress) {
                setMaxProgress(response.progressPercent)
            }
            setCurrentProgress(response.progressPercent)
            setTotalWatchTime(response.totalWatchTimeSec)

            // Check if completed by server (90% auto-complete)
            if (response.isCompleted && !showCompletionActions) {
                setIsVideoCompleted(true)
                setShowCompletionActions(true)
            }

            console.debug(`[VideoStage] Heartbeat sent: pos=${Math.floor(currentPos)}s, progress=${response.progressPercent}%`)

        } catch (error) {
            console.error('Failed to send video heartbeat:', error)
        }
    }, [componentProgressId, maxProgress, showCompletionActions, requiredWatchPercent])

    // Keep sendHeartbeat ref in sync (to avoid interval reset on re-render)
    useEffect(() => {
        sendHeartbeatRef.current = sendHeartbeat
    }, [sendHeartbeat])

    // Video Event function - sends play/pause/seek/end events for pattern analysis
    // 구간 정보 포함: startPositionSec (play 시점) + endPositionSec (이벤트 시점)
    const sendVideoEvent = useCallback(async (
        eventType: VideoEventType,
        startPositionSec: number,
        endPositionSec?: number,
        seekToSec?: number
    ) => {
        if (!componentProgressId) return

        // Debounce: skip if same event type was just sent at similar position
        const lastEvent = lastEventSentRef.current
        const currentPos = endPositionSec ?? startPositionSec
        if (lastEvent && lastEvent.type === eventType && Math.abs(lastEvent.time - currentPos) < 2) {
            return
        }

        try {
            const response = await learningSessionApi.recordVideoEvent(
                componentProgressId,
                {
                    eventType,
                    startPositionSec: Math.floor(startPositionSec),
                    endPositionSec: endPositionSec != null ? Math.floor(endPositionSec) : undefined,
                    seekToSec: seekToSec != null ? Math.floor(seekToSec) : undefined,
                    playbackSpeed: playbackSpeedRef.current,
                    totalDurationSec: Math.floor(durationRef.current),
                    requiredWatchPercent,
                    classProductId,
                }
            )

            lastEventSentRef.current = { type: eventType, time: currentPos }

            // play 이벤트 시 구간 시작 위치 기록
            if (eventType === 'play') {
                playStartPositionRef.current = startPositionSec
            }

            // Check if completed by server
            if (response.isCompleted && !showCompletionActions) {
                setIsVideoCompleted(true)
                setShowCompletionActions(true)
            }

            console.debug(`Video event sent: ${eventType} (${startPositionSec}s → ${endPositionSec ?? '-'}s)`)
        } catch (error) {
            console.error(`Failed to send video event (${eventType}):`, error)
        }
    }, [componentProgressId, showCompletionActions, requiredWatchPercent])

    // Set up heartbeat interval (5 seconds per spec)
    // Using ref to avoid interval reset when sendHeartbeat changes
    useEffect(() => {
        console.debug('[VideoStage] Setting up heartbeat interval')

        // Clear existing interval
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current)
        }

        // Start new interval - use ref to get latest sendHeartbeat
        heartbeatIntervalRef.current = setInterval(() => {
            console.debug('[VideoStage] Heartbeat interval tick')
            sendHeartbeatRef.current?.()
        }, HEARTBEAT_INTERVAL_MS)

        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current)
            }
        }
    }, []) // Empty dependency - interval runs once, uses ref for latest function

    // Get auth token for sendBeacon (needs to include token in request)
    const getAuthToken = useCallback(() => {
        // Get token from localStorage (assuming it's stored there)
        return localStorage.getItem('accessToken') || ''
    }, [])

    // Send position update via sendBeacon (for page unload scenarios)
    const sendBeaconHeartbeat = useCallback(() => {
        if (!componentProgressId || currentTimeRef.current <= 0) return

        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
        const baseUrl = apiBaseUrl.startsWith('http')
            ? new URL(apiBaseUrl).origin
            : window.location.origin

        const url = `${baseUrl}/api/learning/sessions/${componentProgressId}/video-heartbeat`
        const token = getAuthToken()

        const data = JSON.stringify({
            currentPositionSec: Math.floor(currentTimeRef.current),
            totalDurationSec: Math.floor(durationRef.current),
            requiredWatchPercent,
            classProductId,
        })

        // Create blob with headers (sendBeacon doesn't support custom headers directly)
        // Use fetch with keepalive as fallback for authenticated requests
        if (token) {
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-Tenant-Id': 'lgcns', // TODO: get from context
                },
                body: data,
                keepalive: true, // Allows request to outlive the page
            }).catch(() => {
                // Fallback to sendBeacon without auth (may fail with 403)
                navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }))
            })
        } else {
            navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }))
        }
    }, [componentProgressId, getAuthToken, requiredWatchPercent])

    // Send end event when user leaves the page (beforeunload)
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Send heartbeat first to save position
            sendBeaconHeartbeat()

            // Then send end event for pattern analysis
            if (componentProgressId && currentTimeRef.current > 0) {
                const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
                const baseUrl = apiBaseUrl.startsWith('http')
                    ? new URL(apiBaseUrl).origin
                    : window.location.origin

                const url = `${baseUrl}/api/learning/sessions/${componentProgressId}/video-event`
                const token = getAuthToken()

                const data = JSON.stringify({
                    eventType: 'end',
                    startPositionSec: Math.floor(playStartPositionRef.current),
                    endPositionSec: Math.floor(currentTimeRef.current),
                    totalDurationSec: Math.floor(durationRef.current),
                    requiredWatchPercent,
                    classProductId,
                })

                if (token) {
                    fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                            'X-Tenant-Id': 'lgcns',
                        },
                        body: data,
                        keepalive: true,
                    }).catch(() => {
                        navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }))
                    })
                } else {
                    navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }))
                }
            }
        }

        // Handle visibility change (tab switch, minimize)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && currentTimeRef.current > 0) {
                // Save position when page becomes hidden
                sendBeaconHeartbeat()
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [componentProgressId, getAuthToken, sendBeaconHeartbeat])

    // Handle video progress from QueturePlayer
    // This callback is called frequently during playback (timeupdate event)
    const handleVideoProgress = useCallback(
        (progress: number, time: number, videoDuration: number) => {
            const previousTime = currentTimeRef.current
            const wasPlaying = isPlayingRef.current

            // Detect if video is playing (time is advancing)
            const isAdvancing = time > previousTime && Math.abs(time - previousTime) < 2
            if (isAdvancing && !wasPlaying) {
                // Started playing (was paused/stopped, now playing)
                isPlayingRef.current = true
                // play 이벤트: 구간 시작 위치만 전송
                sendVideoEvent('play', time)
                playStartPositionRef.current = time
            } else if (isAdvancing) {
                isPlayingRef.current = true
            }

            // Detect seeking: large time jump (more than 3 seconds)
            if (Math.abs(time - previousTime) > 3 && previousTime > 0) {
                // seek 이벤트: 구간 시작(play 시점) → 구간 종료(seek 직전) → seek 목표
                sendVideoEvent('seek', playStartPositionRef.current, previousTime, time)
                lastHeartbeatTimeRef.current = time // Reset heartbeat start position after seek
                lastHeartbeatTimestampRef.current = Date.now() // Reset wall clock on seek
                playStartPositionRef.current = time // 새 구간 시작
            }

            setCurrentProgress(progress)
            setCurrentTime(time)
            setDuration(videoDuration)

            // Track max progress locally
            if (progress > maxProgress) {
                setMaxProgress(progress)
            }

            // Check for completion threshold
            if (progress >= requiredWatchPercent && !showCompletionActions) {
                setShowCompletionActions(true)
            }
        },
        [maxProgress, showCompletionActions, sendVideoEvent]
    )

    // Detect pause: no progress updates for a while
    useEffect(() => {
        let pauseDetectionTimer: ReturnType<typeof setTimeout> | null = null

        const checkForPause = () => {
            // If no progress update for 2 seconds, consider paused
            pauseDetectionTimer = setTimeout(() => {
                if (isPlayingRef.current && currentTimeRef.current > 0) {
                    // Was playing, now paused - send pause event with 구간 정보
                    sendVideoEvent('pause', playStartPositionRef.current, currentTimeRef.current)
                    // Also save current position via heartbeat (force=true)
                    sendHeartbeat(true)
                }
                isPlayingRef.current = false
            }, 2000)
        }

        // Reset timer on each currentTime change
        if (pauseDetectionTimer) {
            clearTimeout(pauseDetectionTimer)
        }
        checkForPause()

        return () => {
            if (pauseDetectionTimer) {
                clearTimeout(pauseDetectionTimer)
            }
        }
    }, [currentTime, sendVideoEvent, sendHeartbeat])

    // Handle video completion from QueturePlayer (when video ends)
    const handleVideoComplete = useCallback(() => {
        setIsVideoCompleted(true)
        setShowCompletionActions(true)
        // Send end event for pattern analysis (구간 시작 → 구간 종료)
        sendVideoEvent('end', playStartPositionRef.current, currentTimeRef.current)
        // Final heartbeat (force=true to ensure position is saved)
        sendHeartbeat(true)
    }, [sendHeartbeat, sendVideoEvent])

    // Handle re-watch
    const handleRewatch = useCallback(() => {
        setVideoPlayKey((prev) => prev + 1)
        setPlayerStartTime(0)
        setIsVideoCompleted(false)
        setShowCompletionActions(false)
        setCurrentProgress(0)
        setCurrentTime(0)
        lastHeartbeatTimeRef.current = 0
        playStartPositionRef.current = 0
        isPlayingRef.current = false
    }, [])

    // Handle skip blocked (allowSkip=false일 때 앞으로 이동 시도)
    const handleSkipBlocked = useCallback(() => {
        toast.warning(t('learning.video.cannotSkip'))
    }, [])

    // Handle completion confirmation
    const handleConfirmComplete = useCallback(async () => {
        if (isSubmitting || isCompletionConfirmed) return

        setIsSubmitting(true)
        try {
            // Send final heartbeat before completing (force=true)
            await sendHeartbeat(true)

            // Complete the session
            const response = await learningSessionApi.completeSession(
                componentProgressId,
                {
                    componentType: 'video',
                    forceComplete: maxProgress >= requiredWatchPercent,
                    classProductId,
                }
            )

            setIsCompletionConfirmed(true)
            onComplete(response)
        } catch (error) {
            console.error('Failed to complete video session:', error)
        } finally {
            setIsSubmitting(false)
        }
    }, [componentProgressId, maxProgress, isSubmitting, isCompletionConfirmed, onComplete, sendHeartbeat])

    // Format time for display
    const formatTime = (seconds: number): string => {
        if (!seconds || isNaN(seconds)) return '00:00'
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    // Render no video state
    if (!hasVideo) {
        return (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="aspect-video bg-gray-100 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                        <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">{t('learning.video.notReady')}</p>
                        <p className="text-sm mt-2">{t('learning.video.noVideoInfo')}</p>
                    </div>
                </div>
                <div className="p-4 border-t">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('learning.video.goBack')}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Video Header */}
            <div className="px-4 py-3 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onBack}
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                            title={t('learning.video.goBack')}
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h2 className="font-semibold text-gray-900"><ContentMetadataText text={lessonTitle} /></h2>
                            <p className="text-sm text-gray-500">
                                {formatTime(currentTime)} / {formatTime(duration)}
                                {maxProgress > 0 && (
                                    <span className="ml-2 text-indigo-600">
                                        ({t('learning.video.watchedPercent', { percent: Math.round(maxProgress) })})
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Progress Badge */}
                    {showCompletionActions && (
                        <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {t('learning.video.watchCompleted')}
                        </span>
                    )}
                </div>
            </div>

            {/* Video Player */}
            <div className="relative">
                <QueturePlayer
                    renderMetadataMath
                    key={videoPlayKey}
                    qid={videoQid}
                    title={lessonTitle}
                    thumbnailUrl={videoData.thumbnailUrl}
                    hlsUrl={hlsUrl}  // Full HLS URL constructed from API response
                    startTime={playerStartTime}  // 0 on rewatch, lastPositionSec on initial load
                    quetureApi={quetureApiAdapter}
                    onProgress={handleVideoProgress}
                    onComplete={handleVideoComplete}
                    autoPlay={true}
                    allowSkip={import.meta.env.DEV || videoData.skipAllowed}
                    requiredWatchPercent={requiredWatchPercent}
                    onSkipBlocked={handleSkipBlocked}
                />
            </div>

            {/* Rewatch: 종료 버튼 (복습 모드에서 lessonOverview로 이동) */}
            {isRewatch && (
                <div className="p-4 border-t bg-gray-50 flex justify-end">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-5 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('learning.video.exit')}
                    </button>
                </div>
            )}

            {/* Completion Actions (1회차만 표시, 2회차는 이미 완료됨) */}
            {showCompletionActions && !isRewatch && (
                <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-t">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-center sm:text-left">
                            <p className="font-medium text-gray-900">
                                {t('learning.video.watchDone')}
                            </p>
                            <p className="text-sm text-gray-600">
                                {t('learning.video.watchProgress', { percent: Math.round(maxProgress), current: formatTime(currentTime), total: formatTime(duration) })}
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleRewatch}
                                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <RotateCcw className="w-4 h-4" />
                                {t('learning.video.rewatch')}
                            </button>

                            <button
                                onClick={handleConfirmComplete}
                                disabled={isSubmitting || isCompletionConfirmed}
                                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                                    isCompletionConfirmed
                                        ? 'bg-green-600 text-white cursor-default'
                                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                } disabled:opacity-50`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('learning.video.processing')}
                                    </>
                                ) : isCompletionConfirmed ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        {t('learning.video.done')}
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        {t('learning.video.completeLearning')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Progress Bar (1회차만, 복습 시 미표시) */}
            {!isRewatch && !showCompletionActions && maxProgress > 0 && (
                <div className="px-4 py-3 bg-gray-50 border-t">
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 whitespace-nowrap">{t('learning.video.progress')}</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min(maxProgress, 100)}%` }}
                            />
                        </div>
                        <span className="text-sm font-medium text-indigo-600 whitespace-nowrap">
                            {Math.round(maxProgress)}%
                        </span>
                    </div>
                    {maxProgress < requiredWatchPercent && (
                        <p className="text-xs text-gray-500 mt-1">
                            {t('learning.video.requiredWatchNote', { percent: requiredWatchPercent })}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

export default VideoStage
